/**
 * Curated subdivision (state / province / region) lists for the most
 * common countries among EntreDash's expected user base. Lookup is by
 * ISO 3166-1 alpha-2 code (matches `countries.ts`).
 *
 * Countries not in this map fall back to a free-text input on the
 * profile form so that no inventor is blocked from completing
 * registration. We can grow the curated list any time without breaking
 * existing data — already-saved free-text entries remain valid.
 *
 * The values are stored as the subdivision's local short label (e.g.
 * "California", "Ontario", "Greater London") rather than ISO 3166-2
 * codes, because the labels are what users recognize and what we'd
 * surface in admin views.
 */
export const SUBDIVISIONS: Record<string, string[]> = {
  US: [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ],
  CA: [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon',
  ],
  GB: [
    'England',
    'Scotland',
    'Wales',
    'Northern Ireland',
  ],
  AU: [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
  ],
  IN: [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Delhi',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jammu and Kashmir',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
  ],
  MX: [
    'Aguascalientes',
    'Baja California',
    'Baja California Sur',
    'Campeche',
    'Chiapas',
    'Chihuahua',
    'Coahuila',
    'Colima',
    'Durango',
    'Guanajuato',
    'Guerrero',
    'Hidalgo',
    'Jalisco',
    'Mexico City',
    'México (State)',
    'Michoacán',
    'Morelos',
    'Nayarit',
    'Nuevo León',
    'Oaxaca',
    'Puebla',
    'Querétaro',
    'Quintana Roo',
    'San Luis Potosí',
    'Sinaloa',
    'Sonora',
    'Tabasco',
    'Tamaulipas',
    'Tlaxcala',
    'Veracruz',
    'Yucatán',
    'Zacatecas',
  ],
  DE: [
    'Baden-Württemberg',
    'Bavaria',
    'Berlin',
    'Brandenburg',
    'Bremen',
    'Hamburg',
    'Hesse',
    'Lower Saxony',
    'Mecklenburg-Vorpommern',
    'North Rhine-Westphalia',
    'Rhineland-Palatinate',
    'Saarland',
    'Saxony',
    'Saxony-Anhalt',
    'Schleswig-Holstein',
    'Thuringia',
  ],
  FR: [
    'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté',
    'Brittany',
    'Centre-Val de Loire',
    'Corsica',
    'Grand Est',
    'Hauts-de-France',
    'Île-de-France',
    'Normandy',
    'Nouvelle-Aquitaine',
    'Occitanie',
    'Pays de la Loire',
    "Provence-Alpes-Côte d'Azur",
  ],
  IT: [
    'Abruzzo',
    'Aosta Valley',
    'Apulia',
    'Basilicata',
    'Calabria',
    'Campania',
    'Emilia-Romagna',
    'Friuli-Venezia Giulia',
    'Lazio',
    'Liguria',
    'Lombardy',
    'Marche',
    'Molise',
    'Piedmont',
    'Sardinia',
    'Sicily',
    'Trentino-Alto Adige',
    'Tuscany',
    'Umbria',
    'Veneto',
  ],
  ES: [
    'Andalusia',
    'Aragon',
    'Asturias',
    'Balearic Islands',
    'Basque Country',
    'Canary Islands',
    'Cantabria',
    'Castile and León',
    'Castilla-La Mancha',
    'Catalonia',
    'Ceuta',
    'Extremadura',
    'Galicia',
    'La Rioja',
    'Madrid',
    'Melilla',
    'Murcia',
    'Navarre',
    'Valencia',
  ],
  BR: [
    'Acre',
    'Alagoas',
    'Amapá',
    'Amazonas',
    'Bahia',
    'Ceará',
    'Distrito Federal',
    'Espírito Santo',
    'Goiás',
    'Maranhão',
    'Mato Grosso',
    'Mato Grosso do Sul',
    'Minas Gerais',
    'Pará',
    'Paraíba',
    'Paraná',
    'Pernambuco',
    'Piauí',
    'Rio de Janeiro',
    'Rio Grande do Norte',
    'Rio Grande do Sul',
    'Rondônia',
    'Roraima',
    'Santa Catarina',
    'São Paulo',
    'Sergipe',
    'Tocantins',
  ],
  CN: [
    'Anhui',
    'Beijing',
    'Chongqing',
    'Fujian',
    'Gansu',
    'Guangdong',
    'Guangxi',
    'Guizhou',
    'Hainan',
    'Hebei',
    'Heilongjiang',
    'Henan',
    'Hong Kong',
    'Hubei',
    'Hunan',
    'Inner Mongolia',
    'Jiangsu',
    'Jiangxi',
    'Jilin',
    'Liaoning',
    'Macau',
    'Ningxia',
    'Qinghai',
    'Shaanxi',
    'Shandong',
    'Shanghai',
    'Shanxi',
    'Sichuan',
    'Tianjin',
    'Tibet',
    'Xinjiang',
    'Yunnan',
    'Zhejiang',
  ],
  JP: [
    'Aichi',
    'Akita',
    'Aomori',
    'Chiba',
    'Ehime',
    'Fukui',
    'Fukuoka',
    'Fukushima',
    'Gifu',
    'Gunma',
    'Hiroshima',
    'Hokkaido',
    'Hyōgo',
    'Ibaraki',
    'Ishikawa',
    'Iwate',
    'Kagawa',
    'Kagoshima',
    'Kanagawa',
    'Kōchi',
    'Kumamoto',
    'Kyoto',
    'Mie',
    'Miyagi',
    'Miyazaki',
    'Nagano',
    'Nagasaki',
    'Nara',
    'Niigata',
    'Ōita',
    'Okayama',
    'Okinawa',
    'Osaka',
    'Saga',
    'Saitama',
    'Shiga',
    'Shimane',
    'Shizuoka',
    'Tochigi',
    'Tokushima',
    'Tokyo',
    'Tottori',
    'Toyama',
    'Wakayama',
    'Yamagata',
    'Yamaguchi',
    'Yamanashi',
  ],
  KR: [
    'Busan',
    'Chungbuk',
    'Chungnam',
    'Daegu',
    'Daejeon',
    'Gangwon',
    'Gwangju',
    'Gyeongbuk',
    'Gyeonggi',
    'Gyeongnam',
    'Incheon',
    'Jeju',
    'Jeonbuk',
    'Jeonnam',
    'Sejong',
    'Seoul',
    'Ulsan',
  ],
  PH: [
    'Abra',
    'Albay',
    'Bataan',
    'Batangas',
    'Benguet',
    'Bohol',
    'Bulacan',
    'Cagayan',
    'Camarines Sur',
    'Cavite',
    'Cebu',
    'Davao del Sur',
    'Iloilo',
    'Laguna',
    'Leyte',
    'Manila (Metro)',
    'Negros Occidental',
    'Pampanga',
    'Pangasinan',
    'Quezon',
    'Rizal',
    'Tarlac',
    'Zambales',
  ],
  ID: [
    'Aceh',
    'Bali',
    'Banten',
    'Bengkulu',
    'Central Java',
    'Central Kalimantan',
    'Central Sulawesi',
    'East Java',
    'East Kalimantan',
    'East Nusa Tenggara',
    'Gorontalo',
    'Jakarta',
    'Jambi',
    'Lampung',
    'Maluku',
    'North Maluku',
    'North Sulawesi',
    'North Sumatra',
    'Papua',
    'Riau',
    'Riau Islands',
    'South Kalimantan',
    'South Sulawesi',
    'South Sumatra',
    'Southeast Sulawesi',
    'West Java',
    'West Kalimantan',
    'West Nusa Tenggara',
    'West Papua',
    'West Sulawesi',
    'West Sumatra',
    'Yogyakarta',
  ],
  TH: [
    'Bangkok',
    'Chiang Mai',
    'Chiang Rai',
    'Chonburi',
    'Khon Kaen',
    'Krabi',
    'Nakhon Pathom',
    'Nakhon Ratchasima',
    'Nonthaburi',
    'Pathum Thani',
    'Phuket',
    'Rayong',
    'Samut Prakan',
    'Songkhla',
    'Surat Thani',
    'Udon Thani',
  ],
  VN: [
    'An Giang',
    'Bà Rịa-Vũng Tàu',
    'Bắc Giang',
    'Bắc Ninh',
    'Bình Dương',
    'Cần Thơ',
    'Đà Nẵng',
    'Đắk Lắk',
    'Đồng Nai',
    'Hà Nội',
    'Hải Phòng',
    'Ho Chi Minh City',
    'Khánh Hòa',
    'Kiên Giang',
    'Lâm Đồng',
    'Long An',
    'Nghệ An',
    'Quảng Bình',
    'Quảng Nam',
    'Quảng Ninh',
    'Tây Ninh',
    'Thanh Hóa',
  ],
  ZA: [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape',
  ],
  NG: [
    'Abia',
    'Abuja (FCT)',
    'Adamawa',
    'Akwa Ibom',
    'Anambra',
    'Bauchi',
    'Bayelsa',
    'Benue',
    'Borno',
    'Cross River',
    'Delta',
    'Ebonyi',
    'Edo',
    'Ekiti',
    'Enugu',
    'Gombe',
    'Imo',
    'Jigawa',
    'Kaduna',
    'Kano',
    'Katsina',
    'Kebbi',
    'Kogi',
    'Kwara',
    'Lagos',
    'Nasarawa',
    'Niger',
    'Ogun',
    'Ondo',
    'Osun',
    'Oyo',
    'Plateau',
    'Rivers',
    'Sokoto',
    'Taraba',
    'Yobe',
    'Zamfara',
  ],
  EG: [
    'Alexandria',
    'Aswan',
    'Asyut',
    'Beheira',
    'Beni Suef',
    'Cairo',
    'Dakahlia',
    'Damietta',
    'Faiyum',
    'Gharbia',
    'Giza',
    'Ismailia',
    'Kafr El Sheikh',
    'Luxor',
    'Matrouh',
    'Minya',
    'Monufia',
    'New Valley',
    'North Sinai',
    'Port Said',
    'Qalyubia',
    'Qena',
    'Red Sea',
    'Sharqia',
    'Sohag',
    'South Sinai',
    'Suez',
  ],
  AR: [
    'Buenos Aires',
    'Buenos Aires (City)',
    'Catamarca',
    'Chaco',
    'Chubut',
    'Córdoba',
    'Corrientes',
    'Entre Ríos',
    'Formosa',
    'Jujuy',
    'La Pampa',
    'La Rioja',
    'Mendoza',
    'Misiones',
    'Neuquén',
    'Río Negro',
    'Salta',
    'San Juan',
    'San Luis',
    'Santa Cruz',
    'Santa Fe',
    'Santiago del Estero',
    'Tierra del Fuego',
    'Tucumán',
  ],
  CL: [
    'Antofagasta',
    'Arica y Parinacota',
    'Atacama',
    'Aysén',
    'Biobío',
    'Coquimbo',
    'La Araucanía',
    "Libertador General Bernardo O'Higgins",
    'Los Lagos',
    'Los Ríos',
    'Magallanes',
    'Maule',
    'Ñuble',
    'Santiago Metropolitan',
    'Tarapacá',
    'Valparaíso',
  ],
  PK: [
    'Azad Kashmir',
    'Balochistan',
    'Federal Capital Territory (Islamabad)',
    'Gilgit-Baltistan',
    'Khyber Pakhtunkhwa',
    'Punjab',
    'Sindh',
  ],
  TR: [
    'Adana',
    'Ankara',
    'Antalya',
    'Bursa',
    'Eskişehir',
    'Gaziantep',
    'İstanbul',
    'İzmir',
    'Kayseri',
    'Konya',
    'Mersin',
    'Sakarya',
    'Samsun',
    'Trabzon',
  ],
  IL: [
    'Central District',
    'Haifa District',
    'Jerusalem District',
    'Northern District',
    'Southern District',
    'Tel Aviv District',
  ],
  SA: [
    'Al Bahah',
    "Al Jawf",
    "Al Madinah",
    'Al Qassim',
    'Aseer',
    'Eastern Province',
    'Ha\'il',
    'Jazan',
    'Makkah',
    'Najran',
    'Northern Borders',
    'Riyadh',
    'Tabuk',
  ],
  AE: [
    'Abu Dhabi',
    'Ajman',
    'Dubai',
    'Fujairah',
    'Ras Al Khaimah',
    'Sharjah',
    'Umm Al Quwain',
  ],
  SG: [
    'Central Region',
    'East Region',
    'North Region',
    'North-East Region',
    'West Region',
  ],
  HK: [
    'Hong Kong Island',
    'Kowloon',
    'New Territories',
  ],
  CH: [
    'Aargau',
    'Appenzell Ausserrhoden',
    'Appenzell Innerrhoden',
    'Basel-Landschaft',
    'Basel-Stadt',
    'Bern',
    'Fribourg',
    'Geneva',
    'Glarus',
    'Graubünden',
    'Jura',
    'Lucerne',
    'Neuchâtel',
    'Nidwalden',
    'Obwalden',
    'Schaffhausen',
    'Schwyz',
    'Solothurn',
    'St. Gallen',
    'Thurgau',
    'Ticino',
    'Uri',
    'Valais',
    'Vaud',
    'Zug',
    'Zürich',
  ],
  NL: [
    'Drenthe',
    'Flevoland',
    'Friesland',
    'Gelderland',
    'Groningen',
    'Limburg',
    'North Brabant',
    'North Holland',
    'Overijssel',
    'South Holland',
    'Utrecht',
    'Zeeland',
  ],
  BE: [
    'Antwerp',
    'Brussels-Capital',
    'East Flanders',
    'Flemish Brabant',
    'Hainaut',
    'Liège',
    'Limburg',
    'Luxembourg',
    'Namur',
    'Walloon Brabant',
    'West Flanders',
  ],
  SE: [
    'Blekinge',
    'Dalarna',
    'Gävleborg',
    'Gotland',
    'Halland',
    'Jämtland',
    'Jönköping',
    'Kalmar',
    'Kronoberg',
    'Norrbotten',
    'Örebro',
    'Östergötland',
    'Skåne',
    'Södermanland',
    'Stockholm',
    'Uppsala',
    'Värmland',
    'Västerbotten',
    'Västernorrland',
    'Västmanland',
    'Västra Götaland',
  ],
  IE: [
    'Carlow',
    'Cavan',
    'Clare',
    'Cork',
    'Donegal',
    'Dublin',
    'Galway',
    'Kerry',
    'Kildare',
    'Kilkenny',
    'Laois',
    'Leitrim',
    'Limerick',
    'Longford',
    'Louth',
    'Mayo',
    'Meath',
    'Monaghan',
    'Offaly',
    'Roscommon',
    'Sligo',
    'Tipperary',
    'Waterford',
    'Westmeath',
    'Wexford',
    'Wicklow',
  ],
  NZ: [
    'Auckland',
    'Bay of Plenty',
    'Canterbury',
    'Gisborne',
    "Hawke's Bay",
    'Manawatu-Whanganui',
    'Marlborough',
    'Nelson',
    'Northland',
    'Otago',
    'Southland',
    'Taranaki',
    'Tasman',
    'Waikato',
    'Wellington',
    'West Coast',
  ],
};

/**
 * Returns the curated list for a country code, or null if no curated
 * list exists. Callers should fall back to a free-text input when this
 * returns null.
 */
export function subdivisionsFor(countryCode: string): string[] | null {
  return SUBDIVISIONS[countryCode] ?? null;
}
